import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Lightbox from '../shared/Lightbox';
import LazyFacebookVideo from '../shared/LazyFacebookVideo';
import Img from '@/components/shared/Img';

const preparationPhotos = [
{
  src: '/images/dc7a71bb9_645697873_122112497937221049_6554506759712651667_n.jpg',
  alt: 'Terza Costa Construction floor protection and preparation — kitchen remodel Rockport TX',
  label: 'Floor protection — kitchen'
},
{
  src: '/images/9203c7736_646356461_122112497955221049_3220046097236777904_n.jpg',
  alt: 'Bathroom remodel preparation — blue tape and floor covering Rockport TX',
  label: 'Bathroom prep'
},
{
  src: '/images/ab956198a_647263888_122112497919221049_2421224019533847055_n.jpg',
  alt: 'Interior remodel site protection — open concept kitchen and living area Corpus Christi TX',
  label: 'Open concept remodel prep'
},
{
  src: '/images/be5f8108a_647513193_122112497967221049_2493771848642753742_n.jpg',
  alt: 'Hallway floor protection during active remodel — Terza Costa Construction Rockport TX',
  label: 'Hallway protection'
}];


const bathroomAfterPhotos = [
{
  src: '/images/9d0b40d51_654415328_122114816643221049_6555933705371160328_n.jpg',
  alt: 'Completed master bathroom remodel — dual floating vanity with quartz counters and vessel sinks Rockport TX',
  label: 'Completed master bath'
},
{
  src: '/images/40f754988_656018932_122114816673221049_6743965943269587652_n.jpg',
  alt: 'Terza Costa Construction crew finishing bathroom vanity installation Coastal Bend TX',
  label: 'Finish installation'
},
{
  src: '/images/25f521852_656448142_122114816649221049_2752762701803800412_n.jpg',
  alt: 'Master bathroom remodel final result — custom floating vanity quartz backsplash mosaic tile floor Rockport TX',
  label: 'Final result'
}];


const allPhotos = [
  ...bathroomAfterPhotos,
  ...preparationPhotos,
];

export default function VideoSection() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <>
    <section className="py-24 md:py-32 bg-[#0a0a0a] border-t border-black/20">
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14">
          
          <span className="font-body text-[#C97B2E] text-xs font-bold uppercase tracking-[0.3em] mb-4 block">
            See the Work
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Two Full Bathroom Transformations

          </h2>
          <p className="font-body text-white/50 text-base max-w-2xl mx-auto">
            From demolition to a stunning finished result — this master bathroom remodel was 
            completed in under 10 days. Custom floating vanities, quartz counters, vessel sinks, 
            mosaic tile floors, and gold hardware. Happy homeowners.
          </p>
        </motion.div>

        {/* Video + after photos */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start mb-14">

          {/* Portrait video */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 flex flex-col items-center">
            
            <div className="w-full max-w-[300px] mx-auto rounded-sm overflow-hidden shadow-md bg-[#111]">
              <LazyFacebookVideo
                src="https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1380223797204386%2F&show_text=false&width=267&t=0"
                title="Terza Costa bathroom remodel transformation"
                poster="/images/25f521852_656448142_122114816649221049_2752762701803800412_n.jpg"
                posterAlt="Finished master bathroom remodel by Terza Costa Construction — Rockport, TX"
              />
              
            </div>
            <div className="mt-4 text-center max-w-[300px] mx-auto">
              <span className="font-body text-[#C97B2E] text-xs font-bold uppercase tracking-wider block mb-1">
                Bathroom Remodel · Under 10 Days
              </span>
              <p className="font-body text-white/60 text-xs leading-relaxed">
                Watch the full before-to-after transformation.
              </p>
            </div>
          </motion.div>

          {/* After photos — top 1 only (full width on right column) */}
          <div className="lg:col-span-3 grid grid-cols-1 gap-4">
            {bathroomAfterPhotos.slice(0, 1).map((p, i) =>
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="overflow-hidden rounded-sm cursor-pointer"
              onClick={() => setLightbox(i)}>
              
                <Img
                  src={p.src}
                  alt={p.alt}
                  className="w-full object-cover hover:scale-105 transition-transform duration-700"
                  sizes="(min-width: 1024px) 60vw, 100vw"
                  style={{ height: '476px' }}
                />
              
              </motion.div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 mb-14" />

        {/* Preparation photos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8">
          
          <span className="font-body text-[#C97B2E] text-xs font-bold uppercase tracking-[0.3em] mb-3 block text-center">
            Quality Preparation
          </span>
          <h3 className="font-heading text-2xl font-bold text-white text-center mb-2">
            The Work Before the Work
          </h3>
          <p className="font-body text-white/60 text-sm max-w-xl mx-auto text-center mb-8">
            Every jobsite is protected before a single nail is driven — floors covered, 
            surfaces secured, and your home treated with care from day one.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {preparationPhotos.map((p, i) =>
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="overflow-hidden rounded-sm cursor-pointer"
              onClick={() => setLightbox(bathroomAfterPhotos.length + i)}>
              
                <Img
                  src={p.src}
                  alt={p.alt}
                  className="w-full object-cover hover:scale-105 transition-transform duration-700"
                  sizes="(min-width: 640px) 50vw, 100vw"
                  style={{ height: '420px', objectFit: 'cover' }}
                />
              
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* CTA */}
        <div className="text-center mt-8">
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 font-body text-[#C97B2E] text-sm font-bold uppercase tracking-wider hover:text-white transition-colors group">
            
            View Full Project Gallery
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

      </div>
    </section>

    {lightbox !== null && (
      <Lightbox
        images={allPhotos}
        startIndex={lightbox}
        onClose={() => setLightbox(null)}
      />
    )}
  </>
  );

}